import BubbleSvgButton from "../CommonComponents/BubbleSvgButton/BubbleSvgButton";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import useApi from "../../../helpers/apiHelper/requestHelper";
import { toastify } from "../../../helpers/toast/toastify";
import { updateUser } from "../../../redux/users/usersSlice";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import StripePaymentModal from "../StripePaymentModal/index.jsx";
import VerificationApiModal from "../NewCopyTrade/ExchangeConnection/VerificationApiModal";

const SubscriptionPaymentPlanMember = ({
  additionalFee = 0,
  activeTab = null,
  onSubscribe = null, // optional: parent handles checkout (Subscribe.js flow)
  onCancel = null,    // optional: parent handles cancel modal (Subscribe.js flow)
  subscriptions = null, // optional: pre-filtered plans array from parent (Subscribe.js)
  vipSubscription = null, // optional: VIP plan object from parent (Subscribe.js)
}) => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [loading, setLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState({
    show: false,
    buttonClick: "Cancel",
  });

  const user = useSelector((state) => state?.user?.user);
  const dispatch = useDispatch();
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const memberCommunity = user?.memberCommunities?.[0];
  const communityId = memberCommunity?.community?._id;
  const hasActiveSubscription = memberCommunity?.subscribed;
  const subscribedPlanId = memberCommunity?.subscription;

  // Build plans from subscription data with additionalFee applied
  useEffect(() => {
    const subscriptionData =
      user?.memberCommunities?.[0]?.community?.subscription || [];

    const byPrice = (a, b) => Number(a.monthlyfee) - Number(b.monthlyfee);

    // If parent passes pre-filtered subscriptions, use those; otherwise fall back to isActive filter
    const activeSubscriptions = subscriptions
      ? [...subscriptions].sort(byPrice)
      : subscriptionData
          .filter((plan) => plan.isActive === true)
          .sort(byPrice);

    if (activeSubscriptions.length) {
      const billingKey = (activeTab || "Monthly").toLowerCase() === "yearly" ? "yearly" : "monthly";
      const transformed = activeSubscriptions.map((plan) => {
        const leaderFee = Number(
          plan[billingKey === "monthly" ? "monthlyfee" : "yearlyfee"]
        );
        const totalFee = leaderFee + Number(additionalFee || 0);
        return {
          ...plan,
          // Large card price: leader plan only (platform fee not included)
          displayPrice: `$${leaderFee}`,
          // Checkout / CTA still uses total with platform fee
          totalPrice: `$${totalFee}`,
          rawFee: totalFee,
          leaderFee,
          period: billingKey === "monthly" ? "month" : "year",
          max:
            plan.tradingCapital?.toString().toUpperCase() === "UNLIMITED"
              ? "Unlimited"
              : `$${Number(plan.tradingCapital || 0).toLocaleString()}`,
          min: plan.minimumFunding
            ? `$${Number(plan.minimumFunding).toLocaleString()}`
            : null,
          subscribed: hasActiveSubscription && plan._id === subscribedPlanId,
        };
      });

      setPlans(transformed);

      if (!selectedPlan) {
        const alreadySubscribed = transformed.find((p) => p.subscribed);
        setSelectedPlan(alreadySubscribed || transformed[0]);
      }
    }
  }, [activeTab, additionalFee, user, subscriptions]);

  // Handle Stripe return with session_id in URL (self-contained flow only)
  useEffect(() => {
    if (!sessionId || !communityId || onSubscribe) return;
    const handleReturn = async () => {
      setLoading(true);
      try {
        const response = await api("post", "users/handlePayments", { sessionId, communityId });
        if (response?.status) {
          dispatch(updateUser(response?.data?.user));
          toastify("You have been subscribed successfully.", "success");
          navigate(location.pathname, { replace: true });
        } else {
          toastify(response?.message || "Payment not completed.");
        }
      } catch {
        toastify("Something went wrong");
      }
      setLoading(false);
    };
    handleReturn();
  }, [sessionId]);

  // Pay — uses parent callback if provided, else internal checkoutSession
  const handlePay = async (plan) => {
    const target = plan || selectedPlan;
    if (!target) return;

    if (onSubscribe) {
      onSubscribe(target, (activeTab || "Monthly").toLowerCase());
      return;
    }

    setLoading(true);
    try {
      const res = await api("post", "users/checkoutSession", {
        communityId,
        subscription: target,
        plan: (activeTab || "Monthly").toLowerCase() === "yearly" ? "yearly" : "monthly",
      });
      if (res?.status && res?.data?.clientSecret) {
        setClientSecret(res.data.clientSecret);
      } else {
        toastify(res?.message || "Failed to create checkout session");
      }
    } catch {
      toastify("Payment failed. Please try again.");
    }
    setLoading(false);
  };

  // Cancel — uses parent callback if provided, else internal flow
  const handleCancelClick = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    setShowCancelConfirmation({ show: true, buttonClick: "Cancel" });
  };

  const handleConfirmCancel = async () => {
    setCancelLoading(true);
    try {
      const res = await api("post", "users/unsubscribe", {
        sessionId: memberCommunity?.sessionId,
        communityId: memberCommunity?.community?._id,
        userId: user?._id,
      });
      if (res?.status) {
        dispatch(updateUser(res?.data?.user));
        toastify("Subscription cancelled.", "success");
        setSelectedPlan(null);
      } else {
        toastify(res?.message || "Failed to cancel subscription");
      }
    } catch {
      toastify("Failed to cancel subscription");
    }
    setCancelLoading(false);
    setShowCancelConfirmation({ show: false, buttonClick: "" });
  };

  if (clientSecret) {
    return (
      <StripePaymentModal clientSecret={clientSecret} setClientSecret={setClientSecret} />
    );
  }

  const vipPlanType = memberCommunity?.planType || "monthly";
  const vipDisplayPrice = vipPlanType === "yearly" ? "$180" : "$15";
  const vipPeriod = vipPlanType === "yearly" ? "year" : "month";
  const isVipSubscribed = hasActiveSubscription && !!vipSubscription && subscribedPlanId === vipSubscription?._id;

  const totalCards = plans.length + (isVipSubscribed ? 1 : 0);
  const gridClass = `my-grid${totalCards === 2 ? " my-grid--two" : totalCards === 4 ? " my-grid--four" : totalCards === 5 ? " my-grid--five" : ""}`;

  return (
    <>
      <div className={gridClass}>
        {/* VIP subscribed card — first in grid */}
        {isVipSubscribed && (
          <div className="my-grid-item vipCard shadow selected">
            <div className="my-grid-subscribed-badge">SUBSCRIBED</div>
            <h4 className="my-grid-plan-name">VIP Plan</h4>
            <p className="my-grid-plan-desc">Exclusive VIP membership access</p>
            <div className="my-grid-price-row">
              <span className="my-grid-price-amount">{vipDisplayPrice}</span>
              <span className="my-grid-price-period"> / {vipPeriod}</span>
            </div>
            <ul className="my-grid-features">
              {["VIP member access", "Copy trades automatically", "Cancel anytime", "24/7 Support"].map((feat, i) => (
                <li key={i} className="my-grid-feature-item">
                  <span className="my-grid-feature-check">
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  {feat}
                </li>
              ))}
            </ul>
            <div className="my-grid-card-btn-wrap">
              <BubbleSvgButton
                text={cancelLoading ? "Cancelling..." : "Cancel"}
                type="button"
                variant="red"
                containerStyling={{ justifyContent: "center" }}
                mainDivStyle={{ width: "100%", height: "38px" }}
                disabled={cancelLoading}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelClick();
                }}
              />
            </div>
          </div>
        )}

        {plans.map((plan, index) => {
          const features = [
            // additionalFee > 0 ? <span>Platform fee: <strong>${additionalFee}</strong></span> : null,
            plan.max ? <span>Trade with max: <strong>{plan.max}</strong></span> : null,
            plan.min ? <span>Trade with min: <strong>{plan.min}</strong></span> : null,
            "Copy trades automatically",
            "Cancel anytime",
            "24/7 Support",
          ].filter(Boolean);

          const subscribedPlan = plans.find((p) => p.subscribed);
          const isDowngrade = hasActiveSubscription && subscribedPlan && plan.rawFee < subscribedPlan.rawFee;
          const changeLabel = isDowngrade ? "Downgrade" : "Upgrade";

          const btnText = plan.subscribed
            ? cancelLoading ? "Cancelling..." : "Cancel"
            : isVipSubscribed
            ? `Switch to ${plan.totalPrice} / ${plan.period}`
            : hasActiveSubscription
            ? changeLabel
            : loading && selectedPlan?._id === plan._id
            ? "Loading..."
            : `Subscribe for ${plan.totalPrice} / ${plan.period}`;

          const btnVariant = plan.subscribed ? "red" : "black";

          return (
            <div
              className={`my-grid-item shadow${plan.subscribed || selectedPlan?._id === plan._id ? " selected" : ""}`}
              key={plan._id}
              onClick={() => {
                if (hasActiveSubscription) return;
                setSelectedPlan(plan);
              }}
            >
              {/* SUBSCRIBED badge */}
              {plan.subscribed && (
                <div className="my-grid-subscribed-badge">SUBSCRIBED</div>
              )}

              {/* Plan name */}
              <h4 className="my-grid-plan-name">{`Tier ${index + 1}`}</h4>

              {/* Description */}
              <p className="my-grid-plan-desc">
                Trade with up to {plan.max} maximum trading capital
              </p>

              {/* Large price */}
              <div className="my-grid-price-row">
                <span className="my-grid-price-amount">{plan.displayPrice}</span>
                <span className="my-grid-price-period"> / {plan.period}</span>
              </div>

              {/* Feature bullets */}
              <ul className="my-grid-features">
                {features.map((feat, i) => (
                  <li key={i} className="my-grid-feature-item">
                    <span className="my-grid-feature-check">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path
                          d="M1 4L3.5 6.5L9 1"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    {feat}
                  </li>
                ))}
              </ul>

              {/* Per-card action button */}
              <div className="my-grid-card-btn-wrap">
                <BubbleSvgButton
                  text={btnText}
                  type="button"
                  variant={btnVariant}
                  containerStyling={{ justifyContent: "center" }}
                  mainDivStyle={{ width: "100%", height: "38px" }}
                  disabled={plan.subscribed ? cancelLoading : loading && selectedPlan?._id === plan._id}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (plan.subscribed) {
                      handleCancelClick();
                    } else {
                      setSelectedPlan(plan);
                      handlePay(plan);
                    }
                  }}
                />
              </div>
            </div>
          );
        })}

        {!plans.length && !isVipSubscribed && (
          <p className="text-center fw-medium" style={{ marginBottom: "64px" }}>
            Join Community to Subscribe!
          </p>
        )}
      </div>

      {/* Cancel confirmation modal (self-contained flow) */}
      <VerificationApiModal
        showDisConnectPopup={showCancelConfirmation}
        onClose={() => setShowCancelConfirmation({ show: false, buttonClick: "" })}
        disConnectYesAction={handleConfirmCancel}
      />
    </>
  );
};

export default SubscriptionPaymentPlanMember;
